import { useState } from 'react';
import { getScoreColor } from '../utils/scoring';
import TickSlider from './TickSlider';

const CATEGORIES = ['Appetizers', 'Entrées', 'Desserts', 'Cocktails', 'Wine', 'Beer', 'Non-Alcoholic', 'Other'];

const CRITERIA = [
  { id: 'presentation', label: 'Presentation', desc: 'Visual appeal and plating' },
  { id: 'flavor', label: 'Flavor Profile', desc: 'Taste, balance, seasoning' },
  { id: 'value', label: 'Value Perception', desc: 'Price vs quality vs portion' },
  { id: 'trendAlign', label: 'Trend Alignment', desc: 'Current market relevance' },
  { id: 'uniqueness', label: 'Uniqueness', desc: 'Differentiation from competitors' },
  { id: 'profitability', label: 'Profitability', desc: 'Estimated margin and food cost' },
];

export default function MenuItemScoring({ audit, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', category: 'Entrées', price: '' });
  const [selectedCategory, setSelectedCategory] = useState('all');

  const menuItems = audit.menuItems || [];

  const addItem = () => {
    if (!newItem.name.trim()) return;
    const item = {
      id: `mi_${Date.now()}`,
      name: newItem.name.trim(),
      category: newItem.category,
      price: newItem.price,
      scores: {},
      notes: '',
      engineering: '', // star, plowhorse, dog, puzzle
      createdAt: new Date().toISOString(),
    };
    onUpdate({ ...audit, menuItems: [...menuItems, item] });
    setNewItem({ name: '', category: 'Entrées', price: '' });
    setAddingItem(false);
  };

  const updateItemScore = (itemId, criteriaId, value) => {
    const updated = menuItems.map(item =>
      item.id === itemId ? { ...item, scores: { ...item.scores, [criteriaId]: value } } : item
    );
    onUpdate({ ...audit, menuItems: updated });
  };

  const updateItemField = (itemId, field, value) => {
    const updated = menuItems.map(item =>
      item.id === itemId ? { ...item, [field]: value } : item
    );
    onUpdate({ ...audit, menuItems: updated });
  };

  const deleteItem = (itemId) => {
    onUpdate({ ...audit, menuItems: menuItems.filter(i => i.id !== itemId) });
  };

  const getItemAvg = (item) => {
    const vals = Object.values(item.scores || {}).filter(v => v > 0);
    return vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
  };

  const filtered = selectedCategory === 'all' ? menuItems : menuItems.filter(i => i.category === selectedCategory);
  const categories = [...new Set(menuItems.map(i => i.category))];

  // Engineering classification
  const getEngineering = (item) => {
    const avg = getItemAvg(item);
    const profit = item.scores?.profitability || 0;
    const popularity = item.scores?.value || 0;
    if (profit >= 6 && popularity >= 6) return { label: 'Star', color: '#C9A668', icon: '⭐' };
    if (profit >= 6 && popularity < 6) return { label: 'Puzzle', color: '#c084fc', icon: '🧩' };
    if (profit < 6 && popularity >= 6) return { label: 'Plowhorse', color: '#60a5fa', icon: '🐴' };
    return { label: 'Dog', color: '#f87171', icon: '🐕' };
  };

  return (
    <div className="card overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between p-4 hover:bg-neutral-800/30 transition-colors">
        <div className="flex items-center gap-2">
          <span className="text-sm">🍽️</span>
          <span className="text-xs text-neutral-400">Menu Item Scoring</span>
          {menuItems.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-gold/20 text-brand-gold font-medium">{menuItems.length} items</span>
          )}
        </div>
        <span className="text-neutral-600 text-xs">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-neutral-800 pt-3">
          {/* Category filter */}
          {categories.length > 1 && (
            <div className="flex gap-1.5 mb-4 flex-wrap">
              <button onClick={() => setSelectedCategory('all')} className={`text-[10px] px-2 py-1 rounded ${selectedCategory === 'all' ? 'bg-brand-gold/20 text-brand-gold' : 'bg-neutral-800 text-neutral-400'}`}>All ({menuItems.length})</button>
              {categories.map(c => (
                <button key={c} onClick={() => setSelectedCategory(c)} className={`text-[10px] px-2 py-1 rounded ${selectedCategory === c ? 'bg-brand-gold/20 text-brand-gold' : 'bg-neutral-800 text-neutral-400'}`}>
                  {c} ({menuItems.filter(i => i.category === c).length})
                </button>
              ))}
            </div>
          )}

          {/* Add item */}
          {addingItem ? (
            <div className="bg-neutral-800/40 rounded-lg p-3 mb-4">
              <div className="flex gap-2 mb-2">
                <input type="text" value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })} placeholder="Item name..." className="input-field text-sm flex-1" autoFocus />
                <select value={newItem.category} onChange={e => setNewItem({ ...newItem, category: e.target.value })} className="input-field text-sm w-28">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input type="text" value={newItem.price} onChange={e => setNewItem({ ...newItem, price: e.target.value })} placeholder="$" className="input-field text-sm w-16" />
              </div>
              <div className="flex gap-2">
                <button onClick={addItem} className="btn-primary text-xs py-1.5">Add</button>
                <button onClick={() => setAddingItem(false)} className="btn-ghost text-xs">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAddingItem(true)} className="text-xs px-3 py-1.5 rounded bg-neutral-800 text-neutral-400 hover:text-white mb-4">+ Add Menu Item</button>
          )}

          {/* Menu items */}
          <div className="space-y-3">
            {filtered.map(item => {
              const avg = getItemAvg(item);
              const avgColor = getScoreColor(avg);
              const eng = getEngineering(item);

              return (
                <div key={item.id} className="bg-neutral-800/20 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{eng.icon}</span>
                      <div>
                        <span className="text-sm text-white font-medium">{item.name}</span>
                        {item.price && <span className="text-xs text-neutral-500 ml-2">${item.price}</span>}
                      </div>
                      <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: eng.color + '20', color: eng.color }}>{eng.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${avgColor.text}`}>{avg > 0 ? avg.toFixed(1) : '—'}</span>
                      <button onClick={() => deleteItem(item.id)} className="text-[10px] text-neutral-600 hover:text-red-400">✕</button>
                    </div>
                  </div>

                  {/* Criteria scores */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {CRITERIA.map(c => (
                      <div key={c.id} className="bg-neutral-900/50 rounded p-2">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-[9px] text-neutral-500">{c.label}</span>
                          <span className="text-[9px] font-bold" style={{ color: getScoreColor(item.scores?.[c.id] || 0).hex }}>{item.scores?.[c.id] || 0}</span>
                        </div>
                        <input
                          type="range" min="0" max="10" step="0.5"
                          value={item.scores?.[c.id] || 0}
                          onChange={e => updateItemScore(item.id, c.id, parseFloat(e.target.value))}
                          className="tick-slider w-full"
                          style={{ '--pct': `${((item.scores?.[c.id] || 0) / 10) * 100}%`, '--color': getScoreColor(item.scores?.[c.id] || 0).hex, height: '6px' }}
                        />
                      </div>
                    ))}
                  </div>

                  {/* Notes */}
                  <input
                    type="text"
                    value={item.notes || ''}
                    onChange={e => updateItemField(item.id, 'notes', e.target.value)}
                    placeholder="Notes..."
                    className="input-field text-xs mt-2 py-1.5"
                  />
                </div>
              );
            })}
          </div>

          {menuItems.length === 0 && (
            <p className="text-neutral-600 text-sm text-center py-4">Add menu items to score individually.</p>
          )}

          {/* Engineering Summary */}
          {menuItems.length > 0 && (
            <div className="mt-4 pt-3 border-t border-neutral-800">
              <p className="text-[10px] text-neutral-500 font-semibold mb-2 uppercase tracking-wider">Menu Engineering Summary</p>
              <div className="flex gap-3">
                {['Star', 'Puzzle', 'Plowhorse', 'Dog'].map(type => {
                  const count = menuItems.filter(i => getEngineering(i).label === type).length;
                  const eng = { Star: { color: '#C9A668', icon: '⭐' }, Puzzle: { color: '#c084fc', icon: '🧩' }, Plowhorse: { color: '#60a5fa', icon: '🐴' }, Dog: { color: '#f87171', icon: '🐕' } }[type];
                  return (
                    <div key={type} className="flex items-center gap-1.5 text-[10px]" style={{ color: eng.color }}>
                      <span>{eng.icon}</span>
                      <span>{count} {type}{count !== 1 ? 's' : ''}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
